import { secondaryFontFamily, styled, css } from '@openeducation/oe-rn-themes';
import React, { memo, useCallback } from 'react';
import { StyleProp, ViewStyle, View } from 'react-native';

import { LinkText } from '../../atoms/link-text/link-text';
import { flex } from '../../style-utils';

type TooltipButtonType =
  | {
      label: string;
      onPress: () => void;
    }
  | undefined;

type AlignType = 'left' | 'right' | 'top-right' | 'top-left';

export type TooltipProps = {
  buttons?: TooltipButtonType[];
  height?: number;
  width?: number;
  align?: AlignType;
  style?: StyleProp<ViewStyle>;
};

const Tooltip = ({
  buttons = [],
  height = 80,
  width = 175,
  style,
  align = 'left',
}: TooltipProps) => {
  const renderButton = useCallback(
    (button: TooltipButtonType, index: number) =>
      button ? (
        <View style={[flex('flex-row')]} key={`${index}`}>
          <Link label={button.label} onPress={button.onPress} />
        </View>
      ) : null,
    []
  );

  return (
    <Container height={height} width={width} style={style} align={align}>
      {buttons.map(renderButton)}
      <Triangle align={align} />
    </Container>
  );
};

type ContainerType = {
  height: number;
  width: number;
  align: AlignType;
};

const getAlign = (align: AlignType, height: number) => {
  switch (align) {
    case 'right':
      return css`
        right: 0;
        bottom: -${height}px;
      `;
    case 'top-right':
      return css`
        right: 0;
        top: -${height}px;
      `;
    case 'top-left':
      return css`
        left: 0;
        top: -${height}px;
      `;

    case 'left':
    default:
      return css`
        left: 0;
        bottom: -${height}px;
      `;
  }
};
const Container = styled.View<ContainerType>`
  background-color: ${({ theme }) => theme.color.white};
  border-color: ${({ theme }) => theme.color.border};
  border-width: 1px;
  border-radius: 4px;
  position: absolute;
  right: 0;
  justify-content: center;
  align-items: flex-end;
  z-index: 2;
  padding-horizontal: 20px;
  height: ${({ height }) => height}px;
  min-width: ${({ width }) => width}px;
  ${({ align, height }) => getAlign(align, height)};
`;

type TriangleType = {
  align: AlignType;
};

const triangleTop = css`
  bottom: -6px;
  border-bottom-color: ${({ theme }) => theme.color.border};
  border-bottom-width: 1px;
  border-right-color: ${({ theme }) => theme.color.border};
  border-right-width: 1px;
`;
const triangleBottom = css`
  top: -6px;
  border-top-color: ${({ theme }) => theme.color.border};
  border-top-width: 1px;
  border-left-color: ${({ theme }) => theme.color.border};
  border-left-width: 1px;
`;

const Triangle = styled.View<TriangleType>`
  width: 10px;
  height: 10px;
  position: absolute;
  transform: rotate(45deg);
  background-color: ${({ theme }) => theme.color.white};
  ${({ align }) =>
    align === 'right' || align === 'top-right' ? 'right: 14px' : 'left: 14px'};
  ${({ align }) =>
    align === 'top-right' || align === 'top-left'
      ? triangleTop
      : triangleBottom};
`;

const Link = styled(LinkText).attrs(({ theme }) => ({
  fontSize: 14,
  color: theme.color.black,
  fontFamily: secondaryFontFamily,
}))`
  padding-vertical: 4px;
  margin-vertical: 0;
`;

export default memo(Tooltip);
